// Turns clipboard HTML (Word, Google Docs, a web page) into content lines
// so a pasted list keeps its bullets and nesting. Blocks without list
// structure fall back to the plain-text marker rules.

import { MAX_BULLET_LEVEL, parsePastedText, type BulletMarker, type PastedLine } from "@agp/shared";

const BLOCK_TAGS = new Set([
  "P", "DIV", "LI", "H1", "H2", "H3", "H4", "H5", "H6", "TR", "PRE", "BLOCKQUOTE", "SECTION", "ARTICLE", "TD", "TH",
]);

export function parsePastedHtml(html: string): PastedLine[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const out: PastedLine[] = [];
  let current = "";

  // A block with no list context is read like a plain-text row, so "• x"
  // typed into Word still becomes a bullet; a real <li> already knows its
  // level.
  type Ctx = { kind: PastedLine["kind"]; level?: number; marker?: BulletMarker };
  const flush = (ctx: Ctx) => {
    const text = current.replace(/\s+/g, " ").trim();
    current = "";
    if (!text) return;
    out.push(ctx.kind === null ? parsePastedText(text)[0]! : { text, kind: ctx.kind, level: ctx.level, marker: ctx.marker });
  };

  const walk = (node: Node, listDepth: number, ctx: Ctx, listMarker: BulletMarker = "dot") => {
    if (node.nodeType === Node.TEXT_NODE) {
      current += node.textContent ?? "";
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName;
    if (tag === "SCRIPT" || tag === "STYLE" || tag === "HEAD") return;
    if (tag === "BR") {
      flush(ctx);
      return;
    }
    if (tag === "UL" || tag === "OL") {
      flush(ctx);
      // An ordered list numbers its items; a bullet list keeps the marker
      // its style names when it names one ("circle", "square"), else dots.
      const styleType = (el.getAttribute("style") ?? "").match(/list-style-type:\s*([a-z-]+)/)?.[1] ?? el.getAttribute("type") ?? "";
      const marker: BulletMarker =
        tag === "OL"
          ? /^(a|A|lower-alpha|upper-alpha|lower-latin)$/.test(styleType)
            ? "letter"
            : "number"
          : styleType === "circle"
            ? "circle"
            : styleType === "square"
              ? "square"
              : "dot";
      for (const child of el.childNodes) walk(child, listDepth + 1, ctx, marker);
      flush(ctx);
      return;
    }
    if (tag === "LI") {
      flush(ctx);
      const own: Ctx = { kind: "bullet", level: Math.min(MAX_BULLET_LEVEL, Math.max(0, listDepth - 1)), marker: listMarker };
      for (const child of el.childNodes) walk(child, listDepth, own, listMarker);
      flush(own);
      return;
    }
    if (BLOCK_TAGS.has(tag)) {
      flush(ctx);
      for (const child of el.childNodes) walk(child, listDepth, ctx, listMarker);
      flush(ctx);
      return;
    }
    for (const child of el.childNodes) walk(child, listDepth, ctx, listMarker);
  };

  walk(doc.body, 0, { kind: null });
  flush({ kind: null });
  return out;
}

/** What a paste into a single-line field becomes: one line, spaces for
 *  line breaks, list markers dropped. */
export function pastedPlainLine(html: string | null, text: string): string {
  const lines = html ? parsePastedHtml(html) : parsePastedText(text);
  return lines
    .map((l) => l.text)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}
